import { Injectable, inject } from '@angular/core';

import { Page } from '../client-config/page.client-config';
import { LogLevel, LogType } from './logging.models';
import { defaultRemoteLogger } from './remote-logger';

/**
 * @whatItDoes Allows unit testing of logging to console. Similar to `$log` from AngularJS.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class Logger {
    private readonly page = inject(Page);

    debug(value: any, ...rest: any[]) {
        if (this.isEnabledClient(LogLevel.Debug)) {
            const sanitizedMessage = this.sanitizeMessage(value);
            // eslint-disable-next-line no-console
            console.debug(sanitizedMessage, ...rest);
        }
    }

    info(value: any, ...rest: any[]) {
        if (this.isEnabledClient(LogLevel.Info)) {
            const sanitizedMessage = this.sanitizeMessage(value);
            // eslint-disable-next-line no-console
            console.info(sanitizedMessage, ...rest);
        }
    }

    warn(value: any, ...rest: any[]) {
        if (this.isEnabledClient(LogLevel.Warn)) {
            const sanitizedMessage = this.sanitizeMessage(value);
            // eslint-disable-next-line no-console
            console.warn(sanitizedMessage, ...rest);
        }
    }

    error(value: any, ...rest: any[]) {
        if (this.isEnabledClient(LogLevel.Error)) {
            const sanitizedMessage = this.sanitizeMessage(value);
            // eslint-disable-next-line no-console
            console.error(sanitizedMessage, ...rest);
        }
    }

    errorRemote(message: string, error: any) {
        if (this.isEnabledRemote(LogLevel.Error)) {
            const sanitizedMessage = this.sanitizeMessage(message);
            this.error(sanitizedMessage, error);
            defaultRemoteLogger.logError(error, sanitizedMessage);
        }
    }

    warnRemote(message: string) {
        if (this.isEnabledRemote(LogLevel.Warn)) {
            const sanitizedMessage = this.sanitizeMessage(message);
            defaultRemoteLogger.log(sanitizedMessage, LogType.Warning);
        }
    }

    infoRemote(message: string) {
        if (this.isEnabledRemote(LogLevel.Info)) {
            const sanitizedMessage = this.sanitizeMessage(message);
            defaultRemoteLogger.log(sanitizedMessage, LogType.Info);
        }
    }

    sanitizeMessage(message: string): string {
        if (message) {
            const regex = new RegExp(/password:[^,}\n]*([,}]?)/gi);
            return message.replace(regex, (_match, p1) => {
                return p1 === ',' ? '' : p1;
            });
        }

        return message;
    }

    private isEnabledRemote(level: LogLevel): boolean {
        return level === LogLevel.Error || level === LogLevel.Warn || level === LogLevel.Info || !this.page.isProduction || this.page.isInternal;
    }

    private isEnabledClient(level: LogLevel): boolean {
        return this.page.logging?.enabledClientLogLevels ? this.page.logging.enabledClientLogLevels.indexOf(level) > -1 : this.isEnabledRemote(level); // TODO: after af-api rollout we can remove this check
    }
}
